import React, {useState, useEffect} from 'react';
import {useNavigate} from 'react-router-dom';
import {getUserRoomHistory, removeParticipant, RoomHistoryItem} from '../../roomActions';
import {useTranslation} from 'react-i18next';
import styles from './RoomHistory.module.css';
import trashIcon from '../../assets/icon-trash.svg'

interface RoomHistoryProps {
    userId: string;
}

const RoomHistory: React.FC<RoomHistoryProps> = ({userId}) => {
    const [rooms, setRooms] = useState<RoomHistoryItem[]>([]);
    const [isVisible, setIsVisible] = useState(false);
    const {t} = useTranslation();
    const navigate = useNavigate();

    useEffect(() => {
        const loadHistory = async () => {
            if (!userId) {
                return;
            }

            try {
                const history = await getUserRoomHistory(userId);
                setRooms(history);
            } catch (error) {
                console.error('Error loading room history:', error);
            }
        };

        loadHistory();
    }, [userId]);

    const formatDate = (timestamp: any) => {
        if (!timestamp?.toDate) return '';

        const date = timestamp.toDate();

        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const year = date.getFullYear();

        return `${day}-${month}-${year}`;
    };

    const getParticipantCount = (participantIds: string[]) => {
        return participantIds?.length || 0;
    };

    const handleRoomClick = (room: RoomHistoryItem) => {
        switch (room.roomType) {
            case "poker":
                navigate(`/room/${room.id}`);
                break;
            case "bucket":
                navigate(`/bucket/${room.id}`);
                break;
            default:
                console.error("Unexpected room type: ", room.roomType);
        }
    };

    const toggleVisibility = () => {
        setIsVisible(!isVisible);
    };

    const handleRemoveHistoryRoom = async (e: React.MouseEvent, roomId: string) => {
        e.stopPropagation();
        try {
            await removeParticipant(roomId, userId);
            setRooms(prevRooms => prevRooms.filter(room => room.id !== roomId));
        } catch (error) {
            console.error('Error removing participant:', error);
        }
    };

    if (rooms.length === 0) {
        return null;
    }

    return (
        <div className={styles.historyWrp}>
            <div className={`${isVisible ? styles.historyHeaderShow : ''} ${styles.historyHeader}`}>
                <div className={styles.SectionHomeH2}>{t("history")}</div>
                <button
                    className={`${isVisible ? styles.toggleButtonActive : ''} ${styles.toggleButton}`}
                    onClick={toggleVisibility}
                    aria-label={isVisible ? t("hideHistory") : t("showHistory")}
                >
                    {isVisible ? t("hideHistory") : t("showHistory")}
                </button>
            </div>

                <div className={`${styles.historyContent} ${isVisible ? styles.historyContentShow : ''}`}>
                    <div className={styles.roomGrid}>
                        {rooms.map((room) => (
                            <div
                                key={room.id}
                                className={styles.roomCard}
                                onClick={() => handleRoomClick(room)}
                                role="button"
                                tabIndex={0}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                        handleRoomClick(room);
                                    }
                                }}
                            >
                                <div className={styles.roomHeader}>
                                    <div>
                                        <h4 className={styles.roomName}>
                                            {room.roomName || t("room")} {formatDate(room.createdAt)}
                                        </h4>
                                        <span className={styles.participantCount}>
                                            {t("participants", { count: getParticipantCount(room.participantIds) })}
                                        </span>
                                    </div>
                                    <button
                                        className={styles.removeButton}
                                        onClick={(e) => handleRemoveHistoryRoom(e, room.id)}
                                        aria-label={t("removeHistoryRoom")}
                                        title={t("removeHistoryRoom")}
                                    >
                                        <svg width="12" height="14" viewBox="0 0 12 14" fill="none"
                                             xmlns="http://www.w3.org/2000/svg">
                                            <path
                                                d="M0.75 3.47396H1.91667M1.91667 3.47396H11.25M1.91667 3.47396V11.6406C1.91667 11.95 2.03958 12.2468 2.25838 12.4656C2.47717 12.6844 2.77391 12.8073 3.08333 12.8073H8.91667C9.22609 12.8073 9.52283 12.6844 9.74162 12.4656C9.96042 12.2468 10.0833 11.95 10.0833 11.6406V3.47396M3.66667 3.47396V2.30729C3.66667 1.99787 3.78958 1.70113 4.00838 1.48233C4.22717 1.26354 4.52391 1.14063 4.83333 1.14062H7.16667C7.47609 1.14063 7.77283 1.26354 7.99162 1.48233C8.21042 1.70113 8.33333 1.99787 8.33333 2.30729V3.47396"
                                                stroke="white" stroke-width="1.16667" stroke-linecap="round"
                                                stroke-linejoin="round"/>
                                        </svg>
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

        </div>
    );
};

export default RoomHistory;